import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import Select from "react-select";
import { useSelector } from "react-redux";
import { selectUser } from "../../features/userSlice";
import { useNavigate } from "react-router-dom";
import {
  FaSlidersH,
  FaSyncAlt,
  FaTruck,
  FaHome,
  FaArrowUp,
  FaArrowDown,
  FaTimesCircle,
  FaSave,
} from "react-icons/fa";
import styles from "./WbController.module.css";

const sensorOptions = [
  { value: "1", label: "On" },
  { value: "0", label: "Off" },
];

const toSensorValue = (raw) => {
  const v = String(raw ?? "").trim().toUpperCase();
  if (["1", "Y", "YES", "ON", "TRUE"].includes(v)) return "1";
  if (["0", "N", "NO", "OFF", "FALSE"].includes(v)) return "0";
  return "";
};

const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: "44px",
    borderRadius: "12px",
    borderColor: state.isFocused ? "#38bdf8" : "#d8e4f0",
    boxShadow: state.isFocused ? "0 0 0 4px rgba(14,165,233,0.14)" : "none",
    fontSize: "0.94rem",
  }),
  menu: (base) => ({ ...base, zIndex: 9999, borderRadius: "12px", overflow: "hidden" }),
  option: (base, state) => ({
    ...base,
    fontSize: "0.9rem",
    fontWeight: state.isSelected ? 800 : 500,
  }),
};

const WbController = () => {
  const user = useSelector(selectUser);
  const token = user?.token;
  const emp_id = user?.emp_id;
  const navigate = useNavigate();

  const topRef = useRef(null);
  const bottomRef = useRef(null);

  const [rows, setRows] = useState([]);
  const [selectedWb, setSelectedWb] = useState("");
  const [sensor, setSensor] = useState("");
  const [remarks, setRemarks] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState("");
  const [popup, setPopup] = useState({ open: false, title: "", message: "" });

  const showPopup = (message, title = "Notice") =>
    setPopup({ open: true, title, message });
  const closePopup = () => setPopup({ open: false, title: "", message: "" });

  const fetchList = async () => {
    setLoading(true);
    try {
      const res = await axios.post("/api/wbauto/wbController/list", { emp_id, token });
      const data = Array.isArray(res.data) ? res.data : [];
      setRows(data);
      setSelectedWb("");
      setSensor("");
      setRemarks("");
    } catch (err) {
      setRows([]);
      showPopup(err?.response?.data?.message || "Failed to load weighbridges.", "Error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!token) {
      navigate("/");
      return;
    }
    fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(""), 2800);
    return () => clearTimeout(t);
  }, [toast]);

  const wbOptions = useMemo(
    () =>
      rows.map((r) => ({
        value: String(r.WBM_NUMBER_N || ""),
        label: `WB ${r.WBM_NUMBER_N}`,
        row: r,
      })),
    [rows]
  );

  const selected = useMemo(
    () => rows.find((r) => String(r.WBM_NUMBER_N) === String(selectedWb)) || null,
    [rows, selectedWb]
  );

  useEffect(() => {
    if (!selected) {
      setSensor("");
      setRemarks("");
      return;
    }
    setSensor(toSensorValue(selected.Vipl_Sensor));
    setRemarks(String(selected.WBM_Controller_Remarks || "").trim());
  }, [selected]);

  const handleCancel = () => {
    setSelectedWb("");
    setSensor("");
    setRemarks("");
  };

  const handleSubmit = async () => {
    if (!selectedWb) {
      showPopup("Select weighbridge number first.", "Validation");
      return;
    }
    if (!["0", "1"].includes(sensor)) {
      showPopup("Sensor must be On or Off.", "Validation");
      return;
    }
    if (!String(remarks || "").trim()) {
      showPopup("Remarks is required when changing sensor.", "Validation");
      return;
    }

    setSaving(true);
    try {
      const res = await axios.post("/api/wbauto/wbController/updateSensor", {
        emp_id,
        token,
        wbNo: selectedWb,
        sensor,
        remarks: String(remarks).trim(),
      });

      if (res.data?.success) {
        setToast(`✅ ${res.data.message}`);
        await fetchList();
      } else {
        showPopup(res.data?.message || "Update failed.", "Error");
      }
    } catch (err) {
      showPopup(err?.response?.data?.message || "Update failed.", "Error");
    } finally {
      setSaving(false);
    }
  };

  if (!token) {
    return (
      <div className={styles.page}>
        <div className={styles.loginBox}>
          <FaTruck className={styles.loginIcon} />
          <p>You must be logged in.</p>
          <button className={styles.primaryBtn} onClick={() => navigate("/")}>
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div ref={topRef} />

      {toast && <div className={styles.toast}>{toast}</div>}

      {popup.open && (
        <div className={styles.modalBackdrop} onClick={closePopup}>
          <div className={styles.modalSheet} onClick={(e) => e.stopPropagation()}>
            <h4>{popup.title}</h4>
            <p>{popup.message}</p>
            <button className={styles.primaryBtn} onClick={closePopup}>
              OK
            </button>
          </div>
        </div>
      )}

      <div className={styles.hero}>
        <div className={styles.heroIcon}>
          <FaSlidersH />
        </div>
        <div>
          <h2>WB Controller</h2>
          <p>Select weighbridge and turn sensor On / Off.</p>
        </div>
      </div>

      <div className={styles.panel}>
        <div className={styles.sectionTitle}>Select Weighbridge</div>

        <Select
          styles={selectStyles}
          isSearchable
          isClearable
          isDisabled={loading || saving}
          placeholder={loading ? "Loading..." : "Select WB Number"}
          value={wbOptions.find((o) => o.value === selectedWb) || null}
          options={wbOptions}
          onChange={(opt) => setSelectedWb(opt?.value || "")}
        />

        <div className={styles.topButtons}>
          <button
            className={styles.refreshBtn}
            onClick={fetchList}
            disabled={loading || saving}
          >
            <FaSyncAlt /> {loading ? "Refreshing..." : "Refresh"}
          </button>
          <button
            className={styles.cancelBtn}
            onClick={() => navigate("/dashboard")}
            disabled={saving}
          >
            <FaTimesCircle /> Exit
          </button>
        </div>

        <div className={styles.resultMeta}>{rows.length} weighbridges loaded</div>
      </div>

      {selected && (
        <div className={styles.panel}>
          {/* <div className={styles.sectionTitle}>Sensor Control</div> */}
          <div className={styles.formGrid}>
            {/* <div className={styles.formItem}>
              <label>Weighbridge No</label>
              <div className={styles.infoValue}>{selected.WBM_NUMBER_N}</div>
            </div> */}

            {/* <div className={styles.formItem}>
              <label>Current Sensor</label>
              <div className={styles.infoValue}>
                {toSensorValue(selected.Vipl_Sensor) === "1"
                  ? "On"
                  : toSensorValue(selected.Vipl_Sensor) === "0"
                    ? "Off"
                    : selected.Vipl_Sensor || "-"}
              </div>
            </div> */}

            <div className={styles.formItem}>
              <label>Sensor</label>
              <Select
                styles={selectStyles}
                isSearchable={false}
                isDisabled={saving}
                options={sensorOptions}
                value={sensorOptions.find((o) => o.value === sensor) || null}
                onChange={(opt) => setSensor(opt?.value || "")}
                placeholder="Select On / Off"
              />
            </div>

            <div className={styles.formItem}>
              <label>
                Remarks <span className={styles.required}>*</span>
              </label>
              <textarea
                className={styles.textarea}
                rows={3}
                value={remarks}
                disabled={saving}
                placeholder="Enter remarks for this sensor change"
                onChange={(e) => setRemarks(e.target.value)}
              />
            </div>
          </div>

          <div className={styles.actionPanel}>
            <button className={styles.submitBtn} onClick={handleSubmit} disabled={saving}>
              <FaSave /> {saving ? "Saving..." : "Submit"}
            </button>
            <button className={styles.cancelBtn} onClick={handleCancel} disabled={saving}>
              <FaTimesCircle /> Cancel
            </button>
          </div>
        </div>
      )}

      {!selected && !loading && (
        <div className={styles.emptyBox}>Select a weighbridge to change sensor status.</div>
      )}

      <div className={styles.fabStack}>
        <button
          className={styles.fabBtn}
          onClick={() => topRef.current?.scrollIntoView({ behavior: "smooth" })}
        >
          <FaArrowUp />
        </button>
        <button
          className={styles.fabBtn}
          onClick={() => bottomRef.current?.scrollIntoView({ behavior: "smooth" })}
        >
          <FaArrowDown />
        </button>
        <button
          className={`${styles.fabBtn} ${styles.homeFab}`}
          onClick={() => navigate("/dashboard")}
        >
          <FaHome />
        </button>
      </div>

      <div ref={bottomRef} />
    </div>
  );
};

export default WbController;
